import { useCallback, useState, type FormEvent, type ReactElement } from "react";
import { Plus } from "lucide-react";
import { space } from "@tokens/index";
import { Badge, Button, SkeletonRows, Surface, TextInput, useViewport } from "@primitives/index";
import {
    EmptyLine,
    ErrorAlert,
    Field,
    FormActions,
    MutationError,
    SelectableRow,
} from "../admin-ui";
import { useCreateManagementGroup } from "../management-groups";
import type { ManagementGroupSummary } from "../management-groups";
import { cancelOnEscape } from "./escapeCancels";

/**
 * The master list of management groups and the create form above it.
 */

interface ManagementGroupListProps {
    readonly items: readonly ManagementGroupSummary[];
    readonly selectedId: string | null;
    readonly onSelect: (id: string) => void;
    readonly isLoading: boolean;
    readonly error: Error | null;
    readonly onRetry: () => void;
}

function plural(count: number, noun: string): string {
    return `${String(count)} ${noun}${count === 1 ? "" : "s"}`;
}

export function ManagementGroupList({
    items,
    selectedId,
    onSelect,
    isLoading,
    error,
    onRetry,
}: ManagementGroupListProps): ReactElement {
    if (isLoading) {
        return <SkeletonRows rows={4} shape="card" label="Loading management groups" />;
    }
    if (error !== null) {
        return (
            <ErrorAlert onRetry={onRetry}>Couldn&rsquo;t load groups. {error.message}</ErrorAlert>
        );
    }
    if (items.length === 0) {
        return <EmptyLine>No management groups yet — create one above.</EmptyLine>;
    }
    return (
        <nav aria-label="Management groups" style={{ display: "grid", gap: space[2], minWidth: 0 }}>
            {items.map((item) => (
                <SelectableRow
                    key={item.id}
                    selected={item.id === selectedId}
                    onSelect={() => onSelect(item.id)}
                    title={item.name}
                    meta={`${plural(item.adminCount, "admin")} · ${plural(item.activeTeamCount, "team")} · ${plural(item.ownedScopeCount, "scope")}`}
                    badges={
                        item.isSystem ? (
                            <Badge variant="neutral" size="sm">
                                SYSTEM
                            </Badge>
                        ) : undefined
                    }
                />
            ))}
        </nav>
    );
}

interface CreateManagementGroupFormProps {
    readonly onCreated: (id: string) => void;
}

export function CreateManagementGroupForm({
    onCreated,
}: CreateManagementGroupFormProps): ReactElement {
    const { isCompact } = useViewport();
    const create = useCreateManagementGroup();
    const [name, setName] = useState("");
    const [composing, setComposing] = useState(false);

    const handleSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            const trimmed = name.trim();
            if (trimmed.length === 0) return;
            create.mutate(
                { name: trimmed },
                {
                    onSuccess: (group) => {
                        setName("");
                        setComposing(false);
                        onCreated(group.id);
                    },
                },
            );
        },
        [create, name, onCreated],
    );

    const cancel = useCallback(() => {
        if (create.isPending) return;
        setComposing(false);
        setName("");
    }, [create.isPending]);

    if (!composing) {
        return (
            <Button
                variant="primary"
                size="md"
                onClick={() => setComposing(true)}
                fullWidth={isCompact}
            >
                <Plus size={14} strokeWidth={2.25} aria-hidden="true" />
                New management group
            </Button>
        );
    }

    return (
        <Surface elevation={1} borderRadius="lg" padding={4} style={{ width: "100%" }}>
            <form
                onSubmit={handleSubmit}
                onKeyDown={cancelOnEscape(cancel)}
                style={{ display: "grid", gap: space[3] }}
            >
                <Field label="Group name">
                    {(id) => (
                        <TextInput
                            id={id}
                            autoFocus
                            fullWidth
                            placeholder="Group name"
                            value={name}
                            disabled={create.isPending}
                            onChange={(e) => setName(e.target.value)}
                        />
                    )}
                </Field>
                <MutationError error={create.error} />
                <FormActions
                    primary={
                        <Button
                            type="submit"
                            variant="primary"
                            size="md"
                            disabled={name.trim().length === 0}
                            busy={create.isPending}
                        >
                            Create
                        </Button>
                    }
                    secondary={
                        <Button
                            type="button"
                            variant="ghost"
                            size="md"
                            onClick={cancel}
                            disabled={create.isPending}
                        >
                            Cancel
                        </Button>
                    }
                />
            </form>
        </Surface>
    );
}
